import { IbkrAccountSummary } from "../types";
import { DEFAULT_ACCOUNT_DATA_MAX_AGE_MS } from "../config/accountDataConfig";
import httpService from "./httpService";
import type { UserContractExclusion } from "./userContractExclusionsService";
import type { StrategyEntry } from "./investorStrategyService";

export interface AdvisorClientListItem {
  advisorClientId: number;
  userId: string;
  name: string | null;
  email: string;
  ibkrAccountId: string | null;
  groupId: number | null;
  groupName: string | null;
  createdAtUtc: string;
}

export interface CreateClientRequest {
  clientName: string;
  ibkrAccountId: string;
}

export interface CreatedAdvisorClient {
  advisorClient: {
    id: number;
    advisorAccountId: number;
    userId: string;
    groupId: number | null;
    createdAtUtc: string;
    updatedAtUtc: string;
    deletedAtUtc: string | null;
  };
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  advisorClientAccount: {
    id: number;
    advisorAccountId: number;
    userId: string;
    ibkrAccountId: string;
    accountTitle: string;
    displayName: string;
    currency: string;
    type: string;
    isAllocatable: boolean;
  };
}

export interface AdvisorClientListResponse {
  clients: AdvisorClientListItem[];
  advisorAccountId: number | null;
  advisorIbkrAccountId: string | null;
}

const advisorClientService = {
  listClients: async (): Promise<AdvisorClientListResponse> => {
    const response = await httpService.get<{ data: AdvisorClientListItem[]; advisorAccountId?: number; advisorIbkrAccountId?: string }>(
      "/v3/advisor/clients"
    );
    return {
      clients: response.data.data,
      advisorAccountId: response.data.advisorAccountId ?? null,
      advisorIbkrAccountId: response.data.advisorIbkrAccountId ?? null,
    };
  },

  createClient: async (payload: CreateClientRequest): Promise<CreatedAdvisorClient> => {
    const response = await httpService.post<{ data: CreatedAdvisorClient }>(
      "/v3/advisor/clients",
      payload
    );
    return response.data.data;
  },

  deleteClient: async (clientUserId: string): Promise<void> => {
    await httpService.delete(`/v3/advisor/clients/${clientUserId}`);
  },

  listClientsAdmin: async (advisorAccountId: number): Promise<AdvisorClientListResponse> => {
    const response = await httpService.get<{ data: AdvisorClientListItem[]; advisorAccountId?: number; advisorIbkrAccountId?: string }>(
      `/v3/advisor/accounts/${advisorAccountId}/clients`
    );
    return {
      clients: response.data.data,
      advisorAccountId: response.data.advisorAccountId ?? advisorAccountId,
      advisorIbkrAccountId: response.data.advisorIbkrAccountId ?? null,
    };
  },

  createClientAdmin: async (advisorAccountId: number, payload: CreateClientRequest): Promise<CreatedAdvisorClient> => {
    const response = await httpService.post<{ data: CreatedAdvisorClient }>(
      `/v3/advisor/accounts/${advisorAccountId}/clients`,
      payload
    );
    return response.data.data;
  },

  deleteClientAdmin: async (advisorAccountId: number, clientUserId: string): Promise<void> => {
    await httpService.delete(`/v3/advisor/accounts/${advisorAccountId}/clients/${clientUserId}`);
  },

  getClientAccountSummary: async (
    clientUserId: string,
    maxAgeMs: number = DEFAULT_ACCOUNT_DATA_MAX_AGE_MS
  ): Promise<IbkrAccountSummary> => {
    const params = new URLSearchParams();
    params.append("maxAgeMs", maxAgeMs.toString());
    const response = await httpService.post<{ data: IbkrAccountSummary }>(
      `/v3/advisor/clients/${clientUserId}/account-summary?${params.toString()}`
    );
    return response.data.data;
  },

  getClientContractExclusions: async (clientUserId: string): Promise<UserContractExclusion[]> => {
    const response = await httpService.get<{ data: UserContractExclusion[] }>(
      `/v3/advisor/clients/${clientUserId}/contract-exclusions`
    );
    return response.data.data;
  },

  createClientContractExclusion: async (clientUserId: string, conId: number, message?: string): Promise<UserContractExclusion> => {
    const response = await httpService.post<{ data: UserContractExclusion }>(
      `/v3/advisor/clients/${clientUserId}/contract-exclusions`,
      { conId, message }
    );
    return response.data.data;
  },

  deleteClientContractExclusion: async (clientUserId: string, conId: number): Promise<void> => {
    await httpService.delete(`/v3/advisor/clients/${clientUserId}/contract-exclusions/${conId}`);
  },

  deleteAllClientContractExclusions: async (clientUserId: string): Promise<number> => {
    // Advisor endpoint nests the count under data.data.deletedCount (unlike the admin
    // service which reads it top-level). Returns 0 for a client with no exclusions.
    const response = await httpService.delete<{ data: { deletedCount: number } }>(
      `/v3/advisor/clients/${clientUserId}/contract-exclusions`
    );
    return response.data.data.deletedCount ?? 0;
  },

  getClientStrategy: async (clientUserId: string): Promise<StrategyEntry[]> => {
    const response = await httpService.get<{ data: StrategyEntry[] }>(
      `/v3/advisor/clients/${clientUserId}/strategy`
    );
    return response.data.data;
  },
};

export default advisorClientService;
